import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider } from '@ui-framework/index';
import { getSettings } from './api';

function useDocumentSettings(enabled = true) {
  const dataProvider = useDataProvider('issuesTracking');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const requestNumber = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) {
      setSettings(null);
      setLoading(false);
      setError(null);
      return;
    }
    const currentRequest = requestNumber.current + 1;
    requestNumber.current = currentRequest;
    setLoading(true);
    setError(null);
    try {
      const response = await getSettings(dataProvider);
      if (requestNumber.current !== currentRequest) return;
      if (response?.ok && response.data) {
        setSettings(response.data);
      } else {
        setSettings(null);
        setError(response?.errors?.length ? response.errors : [{}]);
      }
    } catch (requestError) {
      if (requestNumber.current !== currentRequest) return;
      setSettings(null);
      setError([{ message: requestError.message }]);
    } finally {
      if (requestNumber.current === currentRequest) setLoading(false);
    }
  }, [dataProvider, enabled]);

  useEffect(() => {
    load();
    return () => {
      requestNumber.current += 1;
    };
  }, [load]);

  return {
    settings: enabled ? settings : null,
    loading: enabled && (loading || (!settings && !error)),
    error: enabled ? error : null,
    refetch: load,
    dataProvider,
  };
}

export default useDocumentSettings;
